import { zodResolver } from '@hookform/resolvers/zod';
import { Memo } from '@kalos/kalos-rpc';
import {
  Button,
  Combobox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kalos/ui';
import { type FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useBatchUserQuery } from '../../../../hooks/react-query/useUserQuery';

// Zod schema for memo editing
const memoEditSchema = z.object({
  userId: z.number({
    required_error: 'Please select a user',
  }),
  classificationId: z.number({
    required_error: 'Please select a category',
  }),
  statusId: z.number({
    required_error: 'Please select a status',
  }),
});

type MemoEditFormData = z.infer<typeof memoEditSchema>;

export interface MemoEditFormProps {
  memo: Memo;
  onSave: (updatedMemo: Memo) => void;
  onCancel: () => void;
  isLoading: boolean;
  memoStatusOptions: any[];
  memoClassificationOptions: any[];
  onMemoChange: (memo: Memo) => void;
  noteNumber?: number;
}

export const MemoEditForm: FC<MemoEditFormProps> = ({
  memo,
  onSave,
  onCancel,
  isLoading,
  memoStatusOptions,
  memoClassificationOptions,
  onMemoChange,
}) => {
  const form = useForm<MemoEditFormData>({
    resolver: zodResolver(memoEditSchema),
    defaultValues: {
      userId: memo.userId,
      classificationId: memo.classificationId,
      statusId: memo.statusId,
    },
  });

  const userBatchGetQuery = useBatchUserQuery({
    filters: {
      isEmployee: 1,
      overrideLimit: true,
    },
  });

  const userOptions = useMemo(() => {
    if (!userBatchGetQuery.data?.results) return [];
    return userBatchGetQuery.data.results.map((user) => ({
      label: `${user.firstname} ${user.lastname}`,
      value: user.id.toString(),
    }));
  }, [userBatchGetQuery.data?.results]);

  const statusOptions = useMemo(() => {
    return memoStatusOptions.map((status) => ({
      label: status.statusDescription,
      value: status.id.toString(),
    }));
  }, [memoStatusOptions]);

  const classificationOptions = useMemo(() => {
    return memoClassificationOptions.map((classification) => ({
      label: classification.categoryDescription,
      value: classification.id.toString(),
    }));
  }, [memoClassificationOptions]);

  const handleFormSubmit = (data: MemoEditFormData) => {
    const updatedMemo = Memo.clone(memo);
    updatedMemo.userId = data.userId;
    updatedMemo.classificationId = data.classificationId;
    updatedMemo.statusId = data.statusId;

    const dirtyFieldsSet = new Set(updatedMemo.fieldMask ?? []);
    if (data.userId !== memo.userId) dirtyFieldsSet.add('UserId');
    if (data.classificationId !== memo.classificationId) dirtyFieldsSet.add('ClassificationId');
    if (data.statusId !== memo.statusId) dirtyFieldsSet.add('StatusId');
    updatedMemo.fieldMask = Array.from(dirtyFieldsSet);

    onMemoChange(updatedMemo);
    onSave(updatedMemo);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="userId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>User</FormLabel>
                <FormControl>
                  <Combobox
                    value={field.value?.toString() || ''}
                    onChange={(value) => {
                      field.onChange(Number(value));
                    }}
                    values={userOptions}
                    placeholder="Select a user..."
                    disabled={isLoading}
                    contentClassName="w-[min(calc(100vw-2rem),var(--radix-popover-trigger-width))] max-w-md"
                    side="bottom"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="classificationId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <FormControl>
                  <Combobox
                    value={field.value?.toString() || ''}
                    onChange={(value) => {
                      field.onChange(Number(value));
                    }}
                    values={classificationOptions}
                    placeholder="Select a category..."
                    disabled={isLoading}
                    contentClassName="w-[min(calc(100vw-2rem),var(--radix-popover-trigger-width))] max-w-md"
                    side="bottom"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="statusId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <FormControl>
                  <Combobox
                    value={field.value?.toString() || ''}
                    onChange={(value) => {
                      field.onChange(Number(value));
                    }}
                    values={statusOptions}
                    placeholder="Select a status..."
                    disabled={isLoading}
                    contentClassName="w-[min(calc(100vw-2rem),var(--radix-popover-trigger-width))] max-w-md"
                    side="bottom"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" type="button" disabled={isLoading} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};
